/**
 * Canonical same-origin validation for post-login "return path" redirects.
 *
 * Every login entry point stashes where the user was headed in a `redirect`
 * query param and, after authentication (and profile completion), sends them
 * back there. That value is attacker-influenceable (it rides in the URL), so
 * before it is ever handed to `router.push`, it must be proven to be a
 * leading-slash, same-origin path. Otherwise `?redirect=https://evil.com` (or
 * the protocol-relative `//evil.com`) becomes an open redirect.
 *
 * This is the single home for that check. Login, the auth guard, and profile
 * completion all route their `redirect` value through `safeReturnPath` rather
 * than trusting `route.query.redirect` directly.
 *
 * The returned value is a **base-relative** route path, because that is what
 * `{ path: ... }` means to vue-router once the history base carries the app
 * prefix. Values minted before that change (and still sitting in delivered
 * emails and bookmarks) carry a literal `/app` prefix, so they are normalized
 * on the way in — permanently, since no deploy ordering reaches them.
 */

import { stripLegacyAppPrefix } from "@/utils/appBase";

// C0 control characters and DEL: browsers strip these, so validating the
// un-stripped form would be misleading. Reject outright.
// eslint-disable-next-line no-control-regex -- intentionally matching control chars
const CONTROL_CHARS = /[\x00-\x1F\x7F]/;

/**
 * Normalize a raw router query value (string, array, null, undefined) to the
 * single string the user intended, or `undefined` when there is nothing usable.
 */
function firstQueryValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === "string");
    return typeof first === "string" ? first : undefined;
  }
  return undefined;
}

/**
 * True only for a well-formed, same-origin, absolute path such as
 * `/club/c1/meeting`. Rejects absolute URLs, protocol-relative URLs
 * (`//host`, `/\host`), and anything containing control characters.
 */
export function isSameOriginReturnPath(candidate: string): boolean {
  if (candidate.length === 0) return false;
  // Must be a path-absolute reference, never protocol-relative or a backslash trick.
  if (!candidate.startsWith("/")) return false;
  if (candidate.startsWith("//") || candidate.startsWith("/\\")) return false;
  if (CONTROL_CHARS.test(candidate)) return false;

  // Resolve against an opaque base: a genuine same-origin path keeps that origin.
  // Protocol-relative and absolute inputs resolve to a different origin and fail.
  try {
    const base = "https://return-path.invalid";
    return new URL(candidate, base).origin === base;
  } catch {
    return false;
  }
}

/**
 * Strip the legacy prefix, then validate. Stripping first is deliberate: a
 * prefixed `//evil.com` is a legitimate same-origin path but normalizes to the
 * protocol-relative `//evil.com`, so validation has to see the normalized form.
 */
function normalizedReturnPath(value: unknown): string | undefined {
  const candidate = firstQueryValue(value);
  if (candidate === undefined) return undefined;
  const normalized = stripLegacyAppPrefix(candidate);
  return isSameOriginReturnPath(normalized) ? normalized : undefined;
}

/**
 * Return the validated, base-relative path from a raw `redirect` query value,
 * or `fallback` when the value is missing or not a safe same-origin path.
 */
export function safeReturnPath(value: unknown, fallback = "/"): string {
  return normalizedReturnPath(value) ?? fallback;
}

/**
 * Like {@link safeReturnPath} but returns `undefined` (not a fallback) when the
 * value is missing or unsafe. Use when the caller needs to distinguish "no
 * valid return path" from "a specific fallback", e.g. to omit the `redirect`
 * query entirely rather than forwarding a bogus one.
 */
export function optionalSafeReturnPath(value: unknown): string | undefined {
  return normalizedReturnPath(value);
}
